import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useEquipment } from '../../../context/EquipmentContext';
import { useTeam } from '../../../context/TeamContext';
import { useFeatureConfig } from '../../../context/FeatureConfigContext';
import { EQUIPMENT_TYPES, EQUIPMENT_STATUS } from '../../../data/nurseAppModels';

const EquipmentListPage = () => {
  const { equipment, catalogs, loading } = useEquipment();
  const { currentTeam } = useTeam();
  const { isFeatureEnabled } = useFeatureConfig();
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  if (!currentTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">กรุณาเลือกทีมก่อน</p>
          <Link
            to="/nurse/teams"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            ไปเลือกทีม
          </Link>
        </div>
      </div>
    );
  }

  const filteredEquipment = equipment.filter(eq => {
    const matchesFilter = filter === 'all' || 
      (filter === 'small' && eq.type === EQUIPMENT_TYPES.SMALL_ITEM) ||
      (filter === 'large' && eq.type === EQUIPMENT_TYPES.LARGE_ITEM);
    const matchesSearch = !searchTerm || 
      eq.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      eq.code?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">อุปกรณ์</h1>
          <div className="flex gap-2">
            {isFeatureEnabled('equipmentManagement') && (
              <>
                <Link
                  to="/nurse/equipment/catalog"
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                >
                  Catalog
                </Link>
                <Link
                  to="/nurse/equipment/create"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  + เพิ่มอุปกรณ์
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <input
              type="text"
              placeholder="ค้นหาอุปกรณ์..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">ทั้งหมด</option>
              <option value="small">ชิ้นเล็ก</option>
              <option value="large">ชิ้นใหญ่</option>
            </select>
          </div>
        </div>

        {/* Equipment List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลด...</p>
          </div>
        ) : filteredEquipment.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <div className="text-6xl mb-4">🩺</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">ยังไม่มีอุปกรณ์</h3>
            <p className="text-gray-600 mb-4">เพิ่มอุปกรณ์ใหม่หรือสร้างจาก Catalog</p>
            <Link
              to="/nurse/equipment/create"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              เพิ่มอุปกรณ์
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEquipment.map(eq => (
              <Link
                key={eq.id}
                to={`/nurse/equipment/${eq.id}`}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">{eq.name}</h3>
                    <p className="text-sm text-gray-600">{eq.code}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded ${
                    eq.type === EQUIPMENT_TYPES.SMALL_ITEM 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {eq.type === EQUIPMENT_TYPES.SMALL_ITEM ? 'ชิ้นเล็ก' : 'ชิ้นใหญ่'}
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  {eq.type === EQUIPMENT_TYPES.SMALL_ITEM ? (
                    <p className="text-gray-600">
                      Stock: {eq.currentStock || 0} {eq.unit}
                    </p>
                  ) : (
                    <p className="text-gray-600">
                      สถานะ: {eq.status === EQUIPMENT_STATUS.AVAILABLE ? 'พร้อมใช้' :
                             eq.status === EQUIPMENT_STATUS.BORROWED ? 'ถูกยืม' :
                             eq.status === EQUIPMENT_STATUS.REPAIR ? 'รอซ่อม' : eq.status}
                    </p>
                  )}
                  <p className="text-gray-600">ตำแหน่ง: {eq.currentLocation || '-'}</p>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/nurse/equipment/inspection"
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center"
          >
            <div className="text-4xl mb-2">🔍</div>
            <h3 className="font-semibold text-gray-800">ตรวจสอบอุปกรณ์</h3>
            <p className="text-sm text-gray-600 mt-2">ตรวจสอบอุปกรณ์ 6 รอบต่อวัน</p>
          </Link>
          <Link
            to="/nurse/equipment/borrow"
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center"
          >
            <div className="text-4xl mb-2">📦</div>
            <h3 className="font-semibold text-gray-800">ยืมอุปกรณ์</h3>
            <p className="text-sm text-gray-600 mt-2">ยืมอุปกรณ์ชิ้นใหญ่</p>
          </Link>
          <Link
            to="/nurse/equipment/dashboard"
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center"
          >
            <div className="text-4xl mb-2">📊</div>
            <h3 className="font-semibold text-gray-800">Dashboard</h3>
            <p className="text-sm text-gray-600 mt-2">สรุปยอดอุปกรณ์รายวัน</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default EquipmentListPage;


